import {
    Component,
    ViewEncapsulation,
    AfterViewInit,
    OnInit
} from "@angular/core";
import {IrbEditor} from "@irb/irb-editors/irb-editors.base";
import {IEvent, IEventSubscriber, IrbAppEvents, IrbDialogsEvents, IrbEditorsEvents, IrbRebalanceEvents} from "@irb-common/events";
import {IrbEditorPortfolioResultService} from "@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.service";
import {
    IHighLevelInfoDto,
    IPortfolioResultDialogData,
    IPortfolioResultRequest,
    IPortfolioResultResponse,
    PrActionState,
    IPortfolioResultChartConfig
} from "@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model";
import {
    PrAllocationsService,
    PrApplicationService,
    PrDialogService,
    PrOrdersService,
    PrValidationService
} from "@irb/irb-editors/editor-portfolio-result/services";
import {ActivatedRoute, Router} from "@angular/router";
import {IAlertDialogData, IApplyFilterOnLanding} from "@irb/app.model";
import {IrbEditorsService} from '@irb/irb-editors/irb-editors.service';
import { IrbAuthenticationService } from '@irb/irb-authentication/irb-authentication.service';
import {AnalyticsService} from "@irb-common/analytics/analytics.service";
import { JasperSettings, ExportPortfolioResults } from "@irb/irb-dialogs/dialog-pr-export-pdf/dialog-pr-export-pdf.component";

@Component({ standalone: false,
    selector: "irb-editor-portfolio-result",
    templateUrl: "./editor-portfolio-result.component.html",
    styleUrls: ["./editor-portfolio-result.component.scss", "../../irb-common/styles/dialog.scss", "../editor-sidebar/editor-sidebar.component.scss"],
    encapsulation: ViewEncapsulation.None,
    providers: [IrbEditorsService]
})
export class EditorPortfolioResultComponent extends IrbEditor implements OnInit, AfterViewInit {
    defaultExpandedPanels: Array<string> = ["SUMMARY", "CASH_VALUES", "ORDERS", "ALLOCATIONS", "ALLOCATION_CHART"];
    ignoreList: Array<string> = ["unheldStatus", "pfSecRebalDTOList"];
    hasError: boolean = false;
    refreshParentGrid: boolean = false;
    userdata: any;
    previousRebelId: number;
    portfolioId: number;
    actionState: any = {};
    needToRemoveQueryParamsOnClose: boolean;
    doNotRemoveQueryParamsOnClose: boolean;
    isTrader:boolean;
    isUiTest: boolean = false;
    portfolioSummaryPosition:any;
    progressTimer: any = null;
    updateStatusCount = 0;
    highLevelInfoDto: IHighLevelInfoDto;
    isAutomated: boolean = false;
    redirectURL: string;
    ENABLE_SEC_GRID_CHART: boolean;
    window: any;
    allocationsTabIndex: number;
    pdfExportConfig: JasperSettings;
    hasExplanation: boolean;

    constructor(
        private prs: IrbEditorPortfolioResultService,
        private pras: PrApplicationService,
        private prds: PrDialogService,
        private prvs: PrValidationService,
        private pros: PrOrdersService,
        private pralloc: PrAllocationsService,
        public localEditorsService: IrbEditorsService,
        private router: Router,
        public ias:IrbAuthenticationService,
        private route: ActivatedRoute,
        public ats: AnalyticsService) {
            super();
            this.identifier = "pfRebalResKeyId";
            this.as.getUserDetails().subscribe((response) => {
                this.userdata = response;
            });
            this.ENABLE_SEC_GRID_CHART = this.as.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_SEC_GRID_CHART || false;
            this.redirectURL = this.as.getItem('IRB_APP_CONFIG')?.veoRedirectUrl;
            this.window = window;
            this.allocationsTabIndex = parseInt(window.localStorage.getItem("PRD_ALLOCATIONS_LAST_TAB_INDEX") || "0");
    }

    ngOnInit(){
        this.ias.getAppConfigDetails().subscribe((data: any) => {
            this.isTrader = data.isTrader;
          });

        this.route.queryParams.subscribe(params => {
            this.previousRebelId = parseInt(params['previousRebalId']);
            this.portfolioId = parseInt(params['portfolio']);
        });
    }
    
    onInputChange(ignoreList?: Array<string>) {
        super.onInputChange(ignoreList);

        if(this.isModified) {
            this.data = {...this.data};
        }

        this.pros.checkForUserEdits(this.data);
        this.pros.checkForOrders(this.data);
    }

    get(event: IEvent) {
        super.get(event);
        this.ats.trackRebalanceResultDetailEvent();
        this.needToRemoveQueryParamsOnClose = false;
        this.doNotRemoveQueryParamsOnClose = event?.data?.request?.doNotRemoveQueryParamsOnClose;
        this.localEditorsService.loadExpansionState(5, "editor-portfolio-result", this.defaultExpandedPanels);
        this.prs.getPortfolioResult(event.data.request).subscribe((data: IPortfolioResultResponse) => {
            this.hasError = (data.errMessage) ? true : false;
            if (!this.hasError) {
                data = this.appendData(data);
            }
            this.load(data);
            setTimeout(() => this.onResize());
            this.pros.checkForUserEdits(data);
            this.pros.checkForOrders(data);
            this.startChecks();
            this.updateActionState();
            this.loadPDFExportConfig();

            const a = (this.data.rebalAccReportList) ? (this.data.rebalAccReportList.length > 0) : false;
            const b = (this.data.rebalSecReportList) ? (this.data.rebalSecReportList.length > 0) : false;
            this.hasExplanation = a || b;
        });
    }

    startChecks() {
        if (this.hasError) {
            this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                severity: 'error',
                summary: 'Message',
                detail: this.data?.errMessage
            });
            if (!this.isUiTest) {
                throw new Error('Editor portfolio result - getPortfolioResult fail. ' + this.data?.errMessage);
            }
        } else {
            this.checkForUnviewedModelChanges();
        }
    }

    checkForUnviewedModelChanges() {
        this.prs.gethighLevelInfoDto(this.data.rebalId).subscribe((res: IHighLevelInfoDto) => {
            this.highLevelInfoDto = res;
            if (res?.scheduleType?.toLowerCase() ==='automated') {
                this.isAutomated = true;
            }
            if (!res.allModelsViewed) {
                this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
                    msg: "You are viewing a result that was run with unviewed model changes.",
                    dCancelBtnLabel: "Continue",
                    dProceedBtnLabel: "Go to Models",
                    onCancel: () => {
                        this.checkForLotMismatch();
                    },
                    onProceed: () => {
                        const state: IApplyFilterOnLanding = {
                            isUpdatRequired: true,
                            filterModel: {name: 'withoutUnread', value: true, isUpdatRequired: true}
                        } as IApplyFilterOnLanding;
                        this.closeWithoutSave();
                        this.router.navigateByUrl("/models/models", {state: state});
                    }
                } as IAlertDialogData);
            } else {
                this.checkForLotMismatch();
            }
        });
    }

    checkForLotMismatch() {
        if (this.data.lotMismatchExist) {
            const s = this.as.getEvents().subscribe((event: IEvent) => {
                if(event.type === IrbDialogsEvents.PR_COST_BASIS_CLOSED) {
                    this.checkForLotMissingCostBasis();
                    s.unsubscribe();
                }
            });
    
            this.as.fireEvent(IrbDialogsEvents.SHOW_PR_COST_BASIS, {
                portfolioName: this.data.portfolioName,
                lotMismatchList: this.data.lotMismatchList
            });
        } else {
           this.checkForLotMissingCostBasis();
        }
    }

    checkForLotMissingCostBasis() {
        if (this.data.lotMissingCostBasisExist) {
            const s = this.as.getEvents().subscribe((event: IEvent) => {
                if(event.type === IrbDialogsEvents.PR_COST_BASIS_CLOSED) {
                    s.unsubscribe();
                }
            });
                    
            this.as.fireEvent(IrbDialogsEvents.SHOW_PR_COST_BASIS, {
                portfolioName: this.data.portfolioName,
                lotMissingCostBasisList: this.data.lotMissingCostBasisList
            });
        }
    }

    get portfolioIds(): number[] {
        return this.getEvent && this.getEvent.data && this.getEvent.data.portfolioIds;
    }

    get isFirstPortfolio(): boolean {
        return this.portfolioIds && this.portfolioIds.indexOf(this.data.portfolioId) === 0;
    }

    get isLastPortfolio(): boolean {
        return this.portfolioIds && this.portfolioIds.indexOf(this.data.portfolioId) === this.portfolioIds.length - 1;
    }

    get getPortfolioPosition(): any{
        this.portfolioSummaryPosition = this.portfolioIds.indexOf(this.data.portfolioId);
        return this.portfolioSummaryPosition + 1;
    } 

    getPreviousPortfolio(): void {
        this.checkForModifications(() =>
            this.goToPortfolio(this.portfolioIds[this.portfolioIds.indexOf(this.data.portfolioId) - 1]));
    }

    getNextPortfolio(): void {
        this.checkForModifications(() =>
            this.goToPortfolio(this.portfolioIds[this.portfolioIds.indexOf(this.data.portfolioId) + 1]));
    }

    appendData(data: IPortfolioResultResponse) {
        this.pralloc.updateAfterAdjValues(data);
        this.pralloc.appendChildEquivalents(data);
        return data;
    }

    savePortfolioResult(req: IPortfolioResultRequest) {
        this.as.fireEvent(IrbEditorsEvents.SAVE_START, {slot: this.slot});

        if (req && (req.method === "saveAndValidateAndApproveTrades"
            || req.method === "saveAndApproveTrades"
            || req.method === "validateAndApproveTrades"
            || req.method === "approveTrades")) {
            this.prs.getPortfolioResultAsync(req).subscribe((resp: any) => {
                if (resp.responseStatus === "SUCCESS") {
                    this.progressTimer && clearTimeout(this.progressTimer);
                    if (resp && resp.responseUtilityMap)
                        this.actionStatusCheckIntervalStart(resp.responseUtilityMap.actionStatusId, req);
                } else {
                    this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
                    if (resp.responseData) { // Only validation is done
                        this.processPostSave(resp.responseData, req);
                        return;
                    }

                    if (!this.isUiTest) {
                        if (resp && resp.responseUtilityMap && resp.responseUtilityMap.errMessage)
                            throw new Error(resp.responseUtilityMap.errMessage);
                        else
                            throw new Error('Editor portfolio result - savePortfolioResult fail. rebalId=' + req.rebalId);
                    }
                }
            }, () => {
                this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
                if (!this.isUiTest) {
                    throw new Error('Editor portfolio result - savePortfolioResult fail. rebalId=' + req.rebalId);
                }
            });
            return;
        }

        this.prs.getPortfolioResult(req).subscribe((resp: IPortfolioResultResponse) => {
            this.postSaveAndRefresh(resp, req);

        }, () => {
            this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
            if (!this.isUiTest) {
                throw new Error('Editor portfolio result - savePortfolioResult fail. rebalId=' + req.rebalId);
            }
        });
    }

    postSaveAndRefresh(resp: any, req: any) {
        this.processPostSave(resp, req);

        this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
            hasPendingFunction: this.hasPendingFunction,
            slot: this.slot
        });
        this.refreshParentGrid = true;
    }

    actionStatusCheckIntervalStart(statusId: any, req: any) {
        this.prs.getActionStatusCheck(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {
                //const response = statusResponse.responseData;
                // let reqStatus = 'FAILED';
                // if (statusResponse.responseUtilityMap && statusResponse.responseUtilityMap.requestStatus)
                //     reqStatus = statusResponse.responseUtilityMap.requestStatus.toUpperCase();
                const response = statusResponse.responseUtilityMap.actionStatus;
                const reqStatus = response.reqStatus.trim().toUpperCase();
                if (reqStatus === 'FAILED') {
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    this.progressTimer && clearTimeout(this.progressTimer);
                    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                        dType: 'ERROR',
                        dTitle: '',
                        dProceedBtnLabel: '',
                        msg: response.failMsg,
                        onProceed: () => {
                        }
                    } as IAlertDialogData);
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    let timeout = this.updateStatusCount > 10 ? 3000 : 1500;
                    if (this.isUiTest)
                        timeout = 0;
                    this.updateStatusCount++;
                    this.progressTimer = setTimeout(() => {
                        this.actionStatusCheckIntervalStart(statusId, req);
                    }, timeout);
                }

                if (reqStatus === 'COMPLETED') {
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    this.progressTimer && clearTimeout(this.progressTimer);

                    this.postSaveAndRefresh(statusResponse.responseData, req);
                }
            } else {
                this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                this.progressTimer && clearTimeout(this.progressTimer);
            }
        }, () => {
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            this.progressTimer && clearTimeout(this.progressTimer);
        });
    }

    processPostSave(resp: IPortfolioResultResponse, req: IPortfolioResultRequest) {

        if (this.prds.isErrorMsg(resp, this.pras.approveMSG)) {
            if (resp.rebalStatus) {
                this.data.rebalStatus = resp.rebalStatus;
                req.method === "recallTrades" && this.data.secRebalDTOList.forEach((secRebalDTO: any) => {
                    secRebalDTO.rebalStatus = resp.rebalStatus;
                    secRebalDTO.rebalStatusEnumValue = resp.rebalStatus;
                });
            }

            if (resp.prevRebalStatus) {
                this.data.prevRebalStatus = resp.prevRebalStatus;
            }

            if (resp.modifiedBy) {
                this.data.modifiedBy = resp.modifiedBy;
            }

            if (resp.modifiedOn) {
                this.data.modifiedOn = resp.modifiedOn;
            }

            this.data.errMessage = resp.errMessage;
            //prdActionPanelObj.switchActionPannel(portfolioSummaryDetails);
            //portfolioSummaryDetails.errMessage = null; //resetting it.
            this.as.fireEvent(IrbEditorsEvents.DATA_CHANGED);
            return;
        }

        this.checkLotLimitation(resp, () => {
            this.checkValidationMessages(resp, (proceedToApprove: boolean) => {
                if(proceedToApprove) {
                    const r: IPortfolioResultRequest = this.pras.getPortfolioResultRequest(this.data, this.isModified, "APPROVE");
                    this.as.fireEvent(IrbRebalanceEvents.SAVE_PORTFOLIO_RESULT_DETAIL, r);
                } else {
                    const dialogData = {} as IPortfolioResultDialogData;
                    dialogData.title = "Status";
                    dialogData.message = resp.resMessage;
                    dialogData.dialogFor = this.pras.action;

                    if (req.method === "saveAndRefuseAndResubmit" || req.method === "refuseAndResubmit") {
                        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
                            msg: "Your rebalance has been submitted.",
                            altMsg: "You can stay on this portfolio detail or go the new rebalance summary for the rebalance that was just submitted.",
                            dCancelBtnLabel: "Cancel - stay on this page",
                            dProceedBtnLabel: "Continue - go to new result",
                            onProceed: () => {
                                this.closeWithoutSave();
                                this.router.navigateByUrl("/rebalancing/rebalance-results", {skipLocationChange: true}).then(() => this.router.navigate(["/rebalancing/result-summary/", resp.rebalId]));
                            }
                        } as IAlertDialogData);
                    } else if (req.method === "saveDetails") {
                        dialogData.message = "Your edits have been saved, but orders haven't been sent to the blotter yet.";
                        this.prvs.displayPfSummryDialog(this.data, dialogData);
                        this.data.revertUserEdits = false;
                    } else {
                        if (req.method !== "saveAndValidateAndApproveTrades" && req.method !== "validateAndApproveTrades" && req.method !== "saveAndApproveTrades" && req.method !== "approveTrades") {
                            this.prvs.displayPfSummryDialog(this.data, dialogData);
                        }
                    }

                    this.data.rebalStatus = resp.rebalStatus;
                    req.method === "recallTrades" && this.data.secRebalDTOList.forEach((secRebalDTO: any) => {
                        secRebalDTO.rebalStatus = resp.rebalStatus;
                        secRebalDTO.rebalStatusEnumValue = resp.rebalStatus;
                    });
                    this.data.prevRebalStatus = resp.prevRebalStatus;
                    this.data.modifiedBy = resp.modifiedBy;
                    this.data.modifiedOn = resp.modifiedOn;
                    this.data.versionId = resp.versionId;
                    this.data.revertUserEdits = resp.revertUserEdits;
                    this.data.userEdit = resp.userEdit; // || this.isModified;

                    this.as.fireEvent(IrbEditorsEvents.DATA_CHANGED);
                    this.load(this.data);
                }
            });
        });
    }

    checkLotLimitation(resp: IPortfolioResultResponse, cbFunc: Function) {
        if(resp.lotLimitationExceedDTOList?.length > 0) {
            this.as.fireEvent(IrbDialogsEvents.SHOW_THRESHOLD_EXCEEDED_DIALOG, {
                portfolios: resp.lotLimitationExceedDTOList,
                callBack: cbFunc
            });
        } else {
            cbFunc();
        }
    }

    checkValidationMessages(resp: IPortfolioResultResponse, cbFunc: Function) {
        if (resp.validnMessages && resp.validnMessages.length !== 0) {
            const confmList = resp.validnMessages;
            const dialogData = {} as IPortfolioResultDialogData;
            dialogData.title = "Confirmation";
            dialogData.dialogFor = "confmValidation";
            dialogData.message = confmList[0];
            dialogData.confmList = confmList;
            dialogData.onComplete = () => {
                cbFunc(true);
            };
            this.prvs.displayPfSummryConfDialog(this.data, null, null, dialogData);
        } else {
            cbFunc(false);
        }
    }

    save() {
        setTimeout(() => {
            if(this.pros.shareQtyUpdateInProgress) {
                setTimeout(() => this.save(),250);
                return;
            }

            this.needToRemoveQueryParamsOnClose = true;
            let r;

            if (this.data.originalOutcome === "UNRESOLVED") {
                r = this.pras.getPortfolioResultRequest(this.data, true, "SAVE");
                this.savePortfolioResult(r);
            } else {
                this.pras.checkNegativeCashAndPerformAction(this.data, "negativeCashAndSave", true, (r: IPortfolioResultRequest) => {
                    this.savePortfolioResult(r);
                });
            }
        }, 250);
    }

    processEvent(event: IEvent) {
        super.processEvent(event);

        switch (event.type) {
            case IrbRebalanceEvents.SAVE_PORTFOLIO_RESULT_DETAIL:
                this.savePortfolioResult(event.data);
                break;
            case IrbRebalanceEvents.NAVIGATE_FROM_PORTFOLIO_RESULT_DETAIL:
                this.navigateFromPortfolioResultDetail(event.data.path, event.data.state);
                break;
            case IrbEditorsEvents.DATA_CHANGED:
                if (this.data) {
                    this.updateActionState();
                    this.pralloc.appendChildEquivalents(this.data);
                    setTimeout(() => this.onResize());
                }
                break;

        }
    }

    exportPDF() {
        this.as.fireEvent((this.ENABLE_SEC_GRID_CHART) ? IrbDialogsEvents.SHOW_PR_EXPORT_PDF : IrbDialogsEvents.SHOW_PR_EXPORT_PDF_LEGACY, {
            rebalanceId: this.data.rebalId,
            rebalanceName: this.data.rebalanceName,
            rebalanceType: this.data.rebalType,
            customCategoryName: this.data.envGlobalSettingsDTO.securityCustAttrAbbr,
            selectedPortfolios: [this.data],
            isDetail: true,
            onContinue: () => {  this.loadPDFExportConfig(); }
        } as ExportPortfolioResults);
    }

    showExplanation() {
        this.as.fireEvent(IrbDialogsEvents.SHOW_PR_EXPLANATION, {
          portfolioName: this.data.portfolioName,
          rebalAccReportList: this.data.rebalAccReportList || [],
          rebalSecReportList: this.data.rebalSecReportList || []
        });
    }

    /*@HostListener("window:resize", ["$event"])
    onResize() {
        if (this.body) {
            const rect = this.body.nativeElement.getBoundingClientRect();
            let h = window.innerHeight - rect.top - 10;

            h = h < 200 ? 200 : h;
            this.bodyHeight = h + "px";
        }
    }*/

    private goToPortfolio(portfolioId: number): void {
        this.get({
            ...this.getEvent,
            data: {
                ...this.getEvent.data,
                request: {
                    ...this.getEvent.data.request, portfolioId: portfolioId
                }
            }
        });
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {portfolio: portfolioId},
            replaceUrl: true
        });
    }

    navigateFromPortfolioResultDetail(path: string, state: IApplyFilterOnLanding) {
        this.checkForModifications(() => {
            this.closeWithoutSave();
            setTimeout(() => { this.router.navigateByUrl(path, {state: state}); });
        });
    }
    cancelChanges() {
        this.needToRemoveQueryParamsOnClose = true;
        this.cancel();
    }
    close(event?: IEvent) {
        super.close();
        if (this.doNotRemoveQueryParamsOnClose) {this.needToRemoveQueryParamsOnClose = false;}
        if (this.needToRemoveQueryParamsOnClose) {
            this.needToRemoveQueryParamsOnClose = false;

            if (!this.previousRebelId) {
                this.router.navigate( [], {
                    relativeTo: this.route,
                    queryParams: null,
                    replaceUrl: true
                });
            } else {
                const previousRebelId = this.previousRebelId;
                const portfolioId = this.portfolioId;
                this.router.navigate(["/rebalancing/result-summary/", previousRebelId], {
                    relativeTo: this.route,
                    queryParams: {portfolio: portfolioId},
                }).then(() => {
                    this.as.fireEvent(IrbRebalanceEvents.SHOW_PORTFOLIO_RESULT_DETAIL, {
                        fullscreen: true,
                        request: {
                            method: "getDetails",
                            rebalId: previousRebelId,
                            portfolioId: portfolioId,
                            secListLoadReqd: true
                        }
                    });
                });
            }


        }
        if (this.refreshParentGrid) {
            this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
            this.refreshParentGrid = false;
        }

        let d = (typeof event !== "undefined") ? event.data : {"slot": this.slot};

        if(!d.source || (d.source && d.source !== "navigation")) {
            this.as.fireEvent(IrbAppEvents.FIRE_TRACKING_EVENT);
        }
    }

    private updateActionState() {
        if (this.data) {
            this.actionState = this.deriveAction({
                actionRequired: true,
                actionTaken: this.getActionTaken(this.data.prevRebalStatus),
                reEnabledOrRecalled: this.isReenabledOrRecalled(this.data.prevRebalStatus),
                isManualAdjust: this.data.rebalType === "Manual Adj",
                isPending: this.data.rebalStatus === "PENDING",
                isNA: this.data.rebalStatus === "NA",
                isApproved: this.data.rebalStatus === "APPROVED",
                isRefused: this.data.rebalStatus === "REFUSED",
                isIgnored: this.data.rebalStatus === "IGNORED",
                hasError: this.data.errMessage && this.data.errMessage !== ''
            });
        }
    }

    getActionTaken(prevRebalStatus: string): string {
        switch (prevRebalStatus) {
            case "APPROVED":
                return "RECALLED";
            case "IGNORED":
                return "RE-ENABLED";
            case "REFUSED":
                return "RE-ENABLED";
        }
    }

    private isReenabledOrRecalled(prevRebalStatus: string) {
        return prevRebalStatus === "APPROVED" || prevRebalStatus === "IGNORED" || prevRebalStatus === "REFUSED";
    }

    handleTradeExplanationChange() {
        this.isModified =  !this.util.compare(this.originalData, this.data, this.ignoreList);
    }

    deriveAction(actionState: PrActionState): PrActionState {
        if (this.data.originalOutcome && this.data.originalOutcome === "UNRESOLVED") {
            actionState.isUnresolved = true;
            actionState.actionRequired = false;
            actionState.ordersProposed = false;
            actionState.userChangedOrders = false;
            if(this.isModified) {
                actionState.userChangedOrders = !this.util.compare(this.originalData.secRebalDTOList,this.data.secRebalDTOList,[],false);
            }
        } else if(!this.isTrader){
            if(this.data.rebalStatus === "APPROVED"){
                actionState.ordersProposed = true;
                actionState.userRemovedOrders = true;
            }
        }else {
            if (this.data.rebalStatus === "PENDING") {
                actionState.actionRequired = true;
                actionState.userChangedOrders = false;
                if (this.data.iRebalAppvStatus === "NA") {
                    actionState.ordersProposed = false;
                } else {
                    actionState.ordersProposed = true;
                }
                if (this.data.userEdit) {
                    actionState.userChangedOrders = true;
                }
                if(this.isModified) {
                    actionState.userChangedOrders = !this.util.compare(this.originalData.secRebalDTOList,this.data.secRebalDTOList,[],false);
                }
            } else if (this.data.rebalStatus === "NA") {
                if (this.data.iRebalAppvStatus === "PENDING") {
                    actionState.actionRequired = false;
                    actionState.ordersProposed = true;
                    actionState.userRemovedOrders = true;
                } if(this.data.rebalType === "Manual Adj"){
                        actionState.ordersProposed = false;
                        actionState.actionRequired = false;
                        actionState.userRemovedOrders = true;
                        if (this.isModified===true){
                                actionState.actionRequired = false;
                                actionState.ordersProposed = false;
                                actionState.userRemovedOrders = false;
                        }
                    }
                actionState.userChangedOrders = false;
                if(this.isModified) {
                    actionState.userChangedOrders = !this.util.compare(this.originalData.secRebalDTOList,this.data.secRebalDTOList,[],false);
                }
            }else if(this.data.rebalStatus === "REFUSED"){
                if(this.data.userEdit){
                    actionState.ordersProposed = true;
                }
            }
            else if (this.data.rebalStatus === "IGNORED" || this.data.rebalStatus === "REFUSED") {
                this.data.unheldStatus = false;
            }
            actionState.isUnresolved = false;
        }
        return actionState;
    }

    openViewPrdAllocations() {
        this.window.open('/rebalancing/view-prd-allocations','view-prd-allocations');
        const bc = new BroadcastChannel('IRB_PRD_CHANNEL');

        bc.onmessage = event => {
            if(event.origin === window.location.origin) {
                if(event.data === "IRB_PRD_READY") {
                    bc.postMessage(this.data);
                }
            }
        }
    }

    openViewPrdAllocationChart() {
        this.window.open('/rebalancing/view-prd-allocation-chart','view-prd-allocation-chart');
        const bc = new BroadcastChannel('IRB_PRD_CHANNEL');

        bc.onmessage = event => {
            if(event.origin === window.location.origin) {
                if(event.data === "IRB_PRD_READY") {
                    bc.postMessage(this.data);
                }
            }
        }
    }

    showModelHistoryInRebalance() {
        let dialogData = {
            portfolioResultData: this.data
        }
        this.as.fireEvent(IrbDialogsEvents.SHOW_MODEL_USED_IN_REBALANCE_DIALOG, dialogData);
    }

    onTabChange(event: any) {
       window.localStorage.setItem("PRD_ALLOCATIONS_LAST_TAB_INDEX",event.index);
       this.pdfExportConfig.allcnGroup = this.pralloc.getAllocGroup(event.index);
       this.onPDFExportConfigChange(this.pdfExportConfig);
    }

    loadPDFExportConfig() {
        this.pdfExportConfig = this.pralloc.getDefaultPDFExportConfig(this.data, this.allocationsTabIndex);
        let c = window.localStorage.getItem("PRD_EXPORT_PDF_CONFIG");

        try {
            JSON.parse(c);
        } catch(e) {
            c = null;
        }

        if(c && this.util.compareObjectKeys(this.pdfExportConfig, JSON.parse(c), ['allcnSortModel','orderSortModel','cashSortModel'])) {
            this.pdfExportConfig = JSON.parse(c);

            if(this.pdfExportConfig.allcnGroupCCName != this.data.envGlobalSettingsDTO?.securityCustAttrAbbr) {
                this.pdfExportConfig.allcnGroupCCName = this.pdfExportConfig.chartGroupCCName = this.data.envGlobalSettingsDTO?.securityCustAttrAbbr;
                this.onPDFExportConfigChange(this.pdfExportConfig);
            }            
        } else {
            this.onPDFExportConfigChange(this.pdfExportConfig);
        }
    }

    onPDFExportConfigChange(ev: JasperSettings) {
        window.localStorage.setItem("PRD_EXPORT_PDF_CONFIG", JSON.stringify(ev));
        this.pdfExportConfig = ev;
    }
}
